import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Select, DatePicker, Empty, Tag } from 'antd';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
    MobileOutlined,
    ClockCircleOutlined,
    PlayCircleOutlined,
    FireOutlined,
    AppleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import GeneralContext from '../../context/GeneralContext/GeneralContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const { RangePicker } = DatePicker;
const { Option } = Select;

// Tipos de treino para exibição
const WORKOUT_TYPE_LABELS = {
    gym: 'Academia',
    home: 'Em casa',
    stretching: 'Alongamento',
};

const WORKOUT_TYPE_COLORS = {
    gym: '#ef4444',
    home: '#3b82f6',
    stretching: '#8b5cf6',
};

// Formata segundos para "Xm Ys"
function formatDuration(seconds) {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m === 0) return `${s}s`;
    if (s === 0) return `${m}min`;
    return `${m}min ${s}s`;
}

export default function AnalyticsScreen() {
    const { isAdmin, loggedUser } = useContext(GeneralContext);

    // Filtros
    const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
    const [clientId, setClientId] = useState(null);
    const [clients, setClients] = useState([]);

    // Dados
    const [sessions, setSessions] = useState(null);
    const [contentViews, setContentViews] = useState(null);
    const [dietViews, setDietViews] = useState(null);
    const [workoutCompletions, setWorkoutCompletions] = useState(null);

    // Loading por seção
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);
    const [loadingDiet, setLoadingDiet] = useState(false);
    const [loadingWorkout, setLoadingWorkout] = useState(false);

    // Carrega lista de clientes para o filtro (apenas admin)
    useEffect(() => {
        if (isAdmin) {
            api.client.list().then(res => setClients(res.data || [])).catch(() => {});
        }
    }, [isAdmin]);

    const buildParams = useCallback(() => ({
        start: dateRange?.[0]?.startOf('day').toISOString(),
        end: dateRange?.[1]?.endOf('day').toISOString(),
        ...(clientId ? { client_id: clientId } : {}),
    }), [dateRange, clientId]);

    const loadAll = useCallback(async () => {
        const params = buildParams();

        setLoadingSessions(true);
        api.analytics.getSessions(params)
            .then(res => setSessions(res.data))
            .catch(() => setSessions(null))
            .finally(() => setLoadingSessions(false));

        setLoadingContent(true);
        api.analytics.getContentViews(params)
            .then(res => setContentViews(res.data))
            .catch(() => setContentViews(null))
            .finally(() => setLoadingContent(false));

        setLoadingDiet(true);
        api.analytics.getDietViews(params)
            .then(res => setDietViews(res.data))
            .catch(() => setDietViews(null))
            .finally(() => setLoadingDiet(false));

        setLoadingWorkout(true);
        api.analytics.getWorkoutCompletions(params)
            .then(res => setWorkoutCompletions(res.data))
            .catch(() => setWorkoutCompletions(null))
            .finally(() => setLoadingWorkout(false));
    }, [buildParams]);

    // Carrega ao montar e ao mudar filtros
    useEffect(() => {
        if (dateRange?.[0] && dateRange?.[1]) {
            loadAll();
        }
    }, [loadAll]);

    // Colunas da tabela de conteúdos
    const contentColumns = [
        {
            title: '#',
            key: 'rank',
            width: 50,
            render: (_, __, index) => <strong>{index + 1}</strong>,
        },
        {
            title: 'Título',
            dataIndex: 'title',
            key: 'title',
            render: (v) => v || '—',
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (v) => {
                const labels = { 1: 'Imagem', 2: 'Vídeo', 3: 'Texto' };
                const colors = { 1: 'blue', 2: 'red', 3: 'green' };
                return <Tag color={colors[v]}>{labels[v] || v}</Tag>;
            },
        },
        {
            title: 'Visualizações',
            dataIndex: 'total_views',
            key: 'total_views',
            sorter: (a, b) => a.total_views - b.total_views,
            defaultSortOrder: 'descend',
            width: 130,
        },
        {
            title: 'Usuários únicos',
            dataIndex: 'unique_users',
            key: 'unique_users',
            width: 130,
        },
    ];

    return (
        <div style={{ padding: '0 4px' }}>
            <h2 style={{ marginBottom: 24 }}>Indicadores de Engajamento</h2>

            {/* Filtros */}
            <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col>
                        <span style={{ marginRight: 8, fontWeight: 500 }}>Período:</span>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            format="DD/MM/YYYY"
                            allowClear={false}
                        />
                    </Col>
                    {isAdmin && clients.length > 0 && (
                        <Col>
                            <span style={{ marginRight: 8, fontWeight: 500 }}>Empresa:</span>
                            <Select
                                style={{ width: 220 }}
                                placeholder="Todas as empresas"
                                allowClear
                                value={clientId}
                                onChange={setClientId}
                            >
                                {clients.map(c => (
                                    <Option key={c.id} value={c.id}>{c.name}</Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* ── Sessões do app ── */}
            <Card
                title={<><MobileOutlined style={{ marginRight: 8 }} />Abertura do App</>}
                style={{ marginBottom: 24 }}
                loading={loadingSessions}
            >
                {sessions ? (
                    <>
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={8}>
                                <Statistic
                                    title="Total de sessões"
                                    value={sessions.total_sessions}
                                    prefix={<MobileOutlined />}
                                />
                            </Col>
                            <Col xs={24} sm={8}>
                                <Statistic
                                    title="Usuários únicos ativos"
                                    value={sessions.unique_users}
                                    prefix={<MobileOutlined />}
                                />
                            </Col>
                            <Col xs={24} sm={8}>
                                <Statistic
                                    title="Taxa de sessões anormais"
                                    value={sessions.abnormal_rate}
                                    suffix="%"
                                    valueStyle={sessions.abnormal_rate > 20 ? { color: '#ef4444' } : {}}
                                />
                            </Col>
                        </Row>

                        {/* ── Tempo de uso ── */}
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12}>
                                <Card size="small" title={<><ClockCircleOutlined style={{ marginRight: 6 }} />Tempo médio (sessões normais)</>}>
                                    <span style={{ fontSize: 28, fontWeight: 700 }}>
                                        {formatDuration(sessions.avg_duration_s)}
                                    </span>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Card size="small" title="Tempo médio (todas as sessões)">
                                    <span style={{ fontSize: 28, fontWeight: 700 }}>
                                        {formatDuration(sessions.avg_duration_all_s)}
                                    </span>
                                </Card>
                            </Col>
                        </Row>

                        {sessions.acessos_por_dia?.length > 0 ? (
                            <Bar
                                style={{ maxHeight: 220 }}
                                data={{
                                    labels: sessions.acessos_por_dia.map(r => r.date),
                                    datasets: [{
                                        label: 'Acessos',
                                        data: sessions.acessos_por_dia.map(r => r.count),
                                        backgroundColor: '#14532d',
                                        borderRadius: 4,
                                    }],
                                }}
                                options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { ticks: { precision: 0 } } } }}
                            />
                        ) : (
                            <Empty description="Sem dados no período" />
                        )}
                    </>
                ) : (
                    <Empty description="Sem dados no período" />
                )}
            </Card>

            {/* ── Conteúdos mais acessados ── */}
            <Card
                title={<><PlayCircleOutlined style={{ marginRight: 8 }} />Conteúdos Mais Acessados</>}
                style={{ marginBottom: 24 }}
                loading={loadingContent}
            >
                {contentViews?.content_views?.length > 0 ? (
                    <Table
                        dataSource={contentViews.content_views}
                        columns={contentColumns}
                        rowKey="content_id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                    />
                ) : (
                    <Empty description="Sem visualizações no período" />
                )}
            </Card>

            {/* ── Dieta ── */}
            <Card
                title={<><AppleOutlined style={{ marginRight: 8 }} />Visualizações de Dieta</>}
                style={{ marginBottom: 24 }}
                loading={loadingDiet}
            >
                {dietViews ? (
                    <>
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12}>
                                <Statistic title="Total de visualizações" value={dietViews.total_views} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <Statistic title="Usuários únicos" value={dietViews.unique_users} />
                            </Col>
                        </Row>
                        {dietViews.visualizacoes_por_dia?.length > 0 ? (
                            <Bar
                                style={{ maxHeight: 200 }}
                                data={{
                                    labels: dietViews.visualizacoes_por_dia.map(r => r.date),
                                    datasets: [{
                                        label: 'Visualizações',
                                        data: dietViews.visualizacoes_por_dia.map(r => r.count),
                                        backgroundColor: '#f97316',
                                        borderRadius: 4,
                                    }],
                                }}
                                options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { ticks: { precision: 0 } } } }}
                            />
                        ) : (
                            <Empty description="Sem dados no período" />
                        )}
                    </>
                ) : (
                    <Empty description="Sem dados no período" />
                )}
            </Card>

            {/* ── Treinos ── */}
            <Card
                title={<><FireOutlined style={{ marginRight: 8 }} />Conclusões de Treino</>}
                style={{ marginBottom: 24 }}
                loading={loadingWorkout}
            >
                {workoutCompletions ? (
                    <>
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={8}>
                                <Statistic title="Total de treinos concluídos" value={workoutCompletions.total_completions} />
                            </Col>
                            <Col xs={24} sm={8}>
                                <Statistic title="Usuários únicos" value={workoutCompletions.unique_users} />
                            </Col>
                            <Col xs={24} sm={8}>
                                <Row gutter={[8, 8]}>
                                    {(workoutCompletions.por_tipo || []).map(item => (
                                        <Col key={item.workout_type}>
                                            <Tag color={WORKOUT_TYPE_COLORS[item.workout_type]}>
                                                {WORKOUT_TYPE_LABELS[item.workout_type] || item.workout_type}: <strong>{item.count}</strong>
                                            </Tag>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>

                        {workoutCompletions.conclusoes_por_dia?.length > 0 ? (
                            <Bar
                                style={{ maxHeight: 200 }}
                                data={{
                                    labels: workoutCompletions.conclusoes_por_dia.map(r => r.date),
                                    datasets: [{
                                        label: 'Conclusões',
                                        data: workoutCompletions.conclusoes_por_dia.map(r => r.count),
                                        backgroundColor: '#8b5cf6',
                                        borderRadius: 4,
                                    }],
                                }}
                                options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { ticks: { precision: 0 } } } }}
                            />
                        ) : (
                            <Empty description="Sem dados no período" />
                        )}
                    </>
                ) : (
                    <Empty description="Sem dados no período" />
                )}
            </Card>
        </div>
    );
}
